import { supabase } from '../lib/supabase';

export const leadScoringService = {
  async getLeadScores(userId) {
    if (!userId) return { data: [], error: 'User ID required' };
    try {
      if (!supabase) return { data: [], error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('funnel_leads')
        ?.select('id, first_name, last_name, email, company, lead_score, is_converted, created_at, source')
        ?.eq('user_id', userId)
        ?.order('lead_score', { ascending: false })
        ?.limit(100);
      if (error) {
        console.warn('Error fetching lead scores:', error?.message);
        return { data: [], error: error?.message };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in getLeadScores:', err);
      return { data: [], error: err?.message };
    }
  },

  /**
   * Get priority queue of leads for AI scoring system
   */
  async getPriorityQueue(userId) {
    if (!userId) return [];
    try {
      if (!supabase) return [];
      const { data, error } = await supabase
        ?.from('funnel_leads')
        ?.select('id, first_name, last_name, email, company, job_title, lead_score, is_converted, created_at, source, phone')
        ?.eq('user_id', userId)
        ?.order('lead_score', { ascending: false })
        ?.limit(50);
      if (error) {
        console.warn('Error fetching priority queue:', error?.message);
        return [];
      }
      return (data || [])?.map(lead => ({
        ...lead,
        name: `${lead?.first_name || ''} ${lead?.last_name || ''}`?.trim() || lead?.email || 'Unknown',
        priority: lead?.lead_score >= 80 ? 'high' : lead?.lead_score >= 50 ? 'medium' : 'low',
        insights: [
          lead?.company ? `Works at ${lead?.company}` : null,
          lead?.job_title ? `Title: ${lead?.job_title}` : null,
          `Score: ${lead?.lead_score || 0}/100`
        ]?.filter(Boolean)
      }));
    } catch (err) {
      console.error('Error in getPriorityQueue:', err);
      return [];
    }
  },

  /**
   * Get scoring factors/configuration
   */
  async getScoringFactors() {
    try {
      return [
        { id: 1, name: 'Email Engagement', weight: 25, description: 'Email open and click rates' },
        { id: 2, name: 'Company Size', weight: 20, description: 'Size of the prospect company' },
        { id: 3, name: 'Job Title', weight: 15, description: 'Seniority and decision-making authority' },
        { id: 4, name: 'Website Activity', weight: 20, description: 'Pages visited and time spent' },
        { id: 5, name: 'Social Engagement', weight: 10, description: 'LinkedIn and social media activity' },
        { id: 6, name: 'Response Rate', weight: 10, description: 'Response to outreach attempts' }
      ];
    } catch (err) {
      console.error('Error in getScoringFactors:', err);
      return [];
    }
  },

  /**
   * Mark a lead as contacted in the priority queue
   */
  async markLeadContacted(leadId) {
    if (!leadId) return { success: false, error: 'Lead ID required' };
    try {
      if (!supabase) return { success: false, error: 'Database not available' };
      const { error } = await supabase
        ?.from('funnel_leads')
        ?.update({ updated_at: new Date()?.toISOString() })
        ?.eq('id', leadId);
      if (error) throw error;
      return { success: true, error: null };
    } catch (err) {
      console.error('Error marking lead as contacted:', err);
      return { success: false, error: err?.message };
    }
  },

  async updateLeadScore(leadId, score, insights) {
    if (!leadId) return { data: null, error: 'Lead ID required' };
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('funnel_leads')
        ?.update({ lead_score: score, updated_at: new Date()?.toISOString() })
        ?.eq('id', leadId)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error updating lead score:', err);
      return { data: null, error: err?.message };
    }
  },

  async getScoringConfig(userId) {
    if (!userId) return { data: null, error: 'User ID required' };
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('lead_scoring_configs')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single();
      if (error && error?.code === 'PGRST116') return { data: null, error: null };
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error fetching scoring config:', err);
      return { data: null, error: err?.message };
    }
  },

  async getLeadScoringStats(userId) {
    if (!userId) return { data: null, error: 'User ID required' };
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('funnel_leads')
        ?.select('id, lead_score, is_converted')
        ?.eq('user_id', userId);
      if (error) throw error;
      const leads = data || [];
      const avgScore = leads?.length > 0
        ? Math.round(leads?.reduce((sum, l) => sum + (l?.lead_score || 0), 0) / leads?.length)
        : 0;
      return {
        data: {
          totalLeads: leads?.length,
          avgScore,
          highScoreLeads: leads?.filter(l => (l?.lead_score || 0) >= 80)?.length,
          convertedLeads: leads?.filter(l => l?.is_converted)?.length
        },
        error: null
      };
    } catch (err) {
      console.error('Error fetching scoring stats:', err);
      return { data: null, error: err?.message };
    }
  }
};

export default leadScoringService;